import { ContractsTaxonomiesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import hasFeature from 'modules/core/lib/hasFeature'

import type { Update } from 'modules/contracts/components/ContractHistory/ContractVersionUpdate'
import { formatInitialCondition } from 'modules/contracts/components/ContractHistory/lib/formatInitialCondition'
import { useContractHistoryDataContext } from 'modules/contracts/lib/contractHistoryDataContext'
import {
  formatAdditionalCompensationUpdate,
  formatCustomFieldUpdate,
} from 'modules/contracts/lib/formatUpdate'
import { useLegalEntityContractData } from 'modules/contracts/lib/legalEntityContractDataContext'
import contractVersionsPayrollPolicies from 'modules/contracts/store/collections/contractVersionsPayrollPolicies'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import { IContractVersion } from 'modules/contracts/store/models/ContractVersion'
import { usePolicies } from 'modules/payroll_bundle/store/collections/policies'

const IGNORED_KEYS = [
  'id',
  'action_type',
  'country',
  'created_at',
  'created_by_avatar',
  'created_by_name',
  'employee_id',
  'ends_on',
  'has_payroll',
  'effective_on',
  'es_contract_observations',
  'es_cotization_group_id',
  'es_tariff_group_id',
  'es_tariff_group_name',
  'es_job_description',
  'fr_jours_par_an',
  'salary_frequency',
  'starts_on',
  'working_hours_frequency',
  'updated_at',
  'annual_working_time_distribution',
]

const DE_KEYS = ['de_contract_type_id']
const PT_KEYS = ['pt_contract_type_id']
const ES_KEYS = [
  'es_contract_type_id',
  'es_education_level_id',
  'es_has_teleworking_contract',
  'es_job_description',
  'es_professional_category_id',
  'es_working_day_type_id',
]
const FR_KEYS = [
  'fr_coefficient',
  'fr_contract_type_id',
  'fr_employee_type',
  'fr_forfait_jours',
  'fr_jours_par_an',
  'fr_jours_par_an_cents',
  'fr_level_id',
  'fr_mutual_id',
  'fr_professional_category',
  'fr_professional_category_id',
  'fr_step_id',
  'fr_work_type_id',
]

const getIgnoredKeys = (country: string): string[] => {
  const ignoredKeys = IGNORED_KEYS

  if (!hasFeature('dev_time_tracking_workable_days_definition')) {
    ignoredKeys.push('bank_holiday_treatment', 'working_time_percentage_in_cents')
  }

  switch (country) {
    case 'es':
      return ignoredKeys.concat(FR_KEYS, DE_KEYS, PT_KEYS)
    case 'fr':
      return ignoredKeys.concat(ES_KEYS, DE_KEYS, PT_KEYS)
    case 'de':
      return ignoredKeys.concat(ES_KEYS, FR_KEYS, PT_KEYS)
    case 'pt':
      return ignoredKeys.concat(ES_KEYS, FR_KEYS, DE_KEYS)
    default:
      return ignoredKeys.concat(ES_KEYS, DE_KEYS, FR_KEYS, PT_KEYS)
  }
}

const getJobIgnoredKeys = (attributes: IContractVersion) => {
  const hasLevel = attributes['job_catalog_level_id'] !== null
  const hasTitle = attributes['job_title'] !== null

  // if both fields are filled we remove one key to remove duplicated
  if (hasLevel && hasTitle) {
    return ['job_catalog_level_id']
  }

  return []
}

export const useInitialConditions = (contractVersion: ContractVersion): Update[] => {
  const {
    access: { locale },
  } = useCurrentSession()
  const {
    additionalCompensations: additionalCompensationsData,
    customFieldValues: customFieldValuesData,
  } = useContractHistoryDataContext()
  const { additionalCompensations } = additionalCompensationsData
  const customFieldValues = customFieldValuesData?.customFieldValues
  const attributes = contractVersion.toJS()
  const policies = usePolicies()

  const legalEntityContractData = useLegalEntityContractData()
  const ignoredKeys = getIgnoredKeys(contractVersion.attributes.country || '')

  const contractVersionCompensations = additionalCompensations.filter(
    (compensation) => compensation.attributes.contract_version_id === contractVersion.id
  )
  const contractVersionCustomFieldValues = customFieldValues?.filter(
    (customField) => customField.attributes.instance_id === contractVersion.id
  )

  const contractPolicies = contractVersionsPayrollPolicies
    .map((payrollPolicy) => {
      return policies.get(payrollPolicy.attributes.payroll_policy_id)
    })
    .filter(Boolean)

  const jobIgnoredKeys = getJobIgnoredKeys(attributes)

  const initialConditions = Object.keys(attributes).reduce((memo: Update[], attr: string) => {
    const ignoreKey = ignoredKeys.includes(attr) || jobIgnoredKeys.includes(attr)
    if (!ignoreKey && attr in contractVersion.attributes) {
      const value = contractVersion.attributes[attr as keyof typeof contractVersion.attributes]
      if (value !== null) {
        memo.push(formatInitialCondition(attr, legalEntityContractData, contractVersion))
      }
    }

    return memo
  }, [])
  const { data } = useQuery(ContractsTaxonomiesDocument, {
    variables: {
      ids: additionalCompensations.map(
        (compensation) => compensation.attributes.contracts_taxonomy_id
      ),
    },
  })

  contractVersionCompensations.forEach((compensation) => {
    const taxonomy = data?.contracts.taxonomies.find(
      (taxo) => taxo.id === compensation.attributes.contracts_taxonomy_id
    )
    initialConditions.push(
      formatAdditionalCompensationUpdate(
        'new_additional_compensation',
        compensation,
        contractVersion,
        locale,
        taxonomy
      )
    )
  })

  initialConditions.push({
    key: 'payroll_policy',
    currentValue: contractPolicies[0]?.attributes.name,
  })

  contractVersionCustomFieldValues?.forEach((customField) => {
    initialConditions.push(formatCustomFieldUpdate('new_custom_field', customField))
  })

  return initialConditions
}
